import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Sparkles, 
  Droplets,
  Hand,
  Eye,
  Scissors,
  Waves,
  ArrowRight,
  Heart,
} from "lucide-react";

const massagePrices = [
  { name: "Hot Stone Massage", price: "$18,000" },
  { name: "Full Body Massage (leg, back, head, foot) — 1 hour", price: "$20,000" },
  { name: "Back Massage — 20 min", price: "$7,000" },
  { name: "Foot & Leg Massage — 20 min", price: "$8,000" },
];

const facialPrices = [
  { name: "Classic Facial", price: "$7,000" },
  { name: "Deep Cleaning Facial", price: "$11,000" },
  { name: "Acne Facial", price: "$11,000" },
  { name: "LED Therapy Facial", price: "$10,000" },
  { name: "High Frequency Facial", price: "$9,000" },
  { name: "Nano Needling Facial", price: "$14,000" },
  { name: "Microdermabrasion Facial", price: "$15,000" },
  { name: "Mild Chemical Facial", price: "$12,000" },
  { name: "Back Facial", price: "$8,000" },
  { name: "Deep Cleaning Back Facial", price: "$10,000" },
  { name: "Brightening Back Facial", price: "$13,000" },
];

const nailPrices = [
  { name: "Gel-X Short", price: "$8,000" },
  { name: "Gel-X Medium", price: "$10,000" },
  { name: "Acrylic Nails Short", price: "$7,000 – $12,000" },
  { name: "Acrylic Nails Medium", price: "$10,000 – $15,000" },
  { name: "Acrylic Nails Long", price: "$12,000 – $18,000" },
  { name: "Soak Off", price: "$2,000" },
  { name: "Builder Gel on Toes", price: "$3,000" },
];

const maniPediPrices = [
  { name: "Spa Manicure", price: "$5,000" },
  { name: "Gel Manicure", price: "$8,000" },
  { name: "Classic Pedicure", price: "$7,000" },
  { name: "Jelly Pedicure", price: "$10,000" },
  { name: "Luxury Spa Pedicure", price: "$12,000" },
];

const lashPrices = [
  { name: "Hybrid", price: "$10,000" },
  { name: "Volume", price: "$13,000" },
  { name: "Classic", price: "$15,000" },
  { name: "Cluster Lash", price: "$3,000" },
  { name: "Refill", price: "$3,000" },
];

const waxingPrices = [
  { name: "Brow", price: "$1,500" },
  { name: "Upper Lip", price: "$2,000" },
  { name: "Full Face", price: "$7,000" },
  { name: "Underarm", price: "$3,500" },
  { name: "Half Leg", price: "$7,000" },
  { name: "Full Leg", price: "$11,000" },
  { name: "Bikini Line", price: "$4,000" },
  { name: "Brazilian", price: "$12,000" },
  { name: "Brazilian Hollywood", price: "$14,000" },
  { name: "Brazilian Butt Strip", price: "$5,000 – $7,000" },
  { name: "Brazilian Cheeks + Strip", price: "$10,000 – $15,000" },
];

const hairChemicalPrices = [
  { name: "Relaxer (Touch-up)", price: "$8,000 – $15,000" },
  { name: "Virgin Relaxer", price: "$12,000 – $20,000" },
  { name: "Keratin Treatment", price: "$30,000 – $45,000" },
  { name: "Deep Conditioning", price: "$6,000 – $15,000" },
  { name: "Protein Treatment", price: "$10,000 – $20,000" },
  { name: "Steam Treatment", price: "$10,000 – $20,000" },
  { name: "Hair Trim", price: "$2,500" },
];

const hairColorPrices = [
  { name: "Full Color (Short Hair)", price: "$8,000 – $15,000" },
  { name: "Root Touch-up", price: "$5,000 – $10,000" },
  { name: "Full Color (Medium Hair)", price: "$15,000 – $30,000" },
  { name: "Full Color (Long / Thick Hair)", price: "$30,000 – $45,000" },
];

const otherServices = [
  {
    category: "Hair Services",
    icon: Scissors,
    items: [
      { name: "Hair Coloring", description: "Professional color application" },
      { name: "Streak", description: "$15,000 – $25,000" },
      { name: "Toner (Blonde Ash Finish)", description: "$10,000" },
      { name: "Hair Bleaching", description: "Lightening and highlights" },
      { name: "Silk Press", description: "Smooth, silky straight finish" },
      { name: "Basic Ponytail", description: "$6,000 – $10,000" },
      { name: "Styled / High Ponytail (Glam)", description: "$8,000 – $15,000" },
      { name: "Hair Straightening", description: "Long-lasting straight style" },
    ]
  },
  {
    category: "Locs Detox",
    icon: Scissors,
    items: [
      { name: "Short Locs Detox", description: "$8,000 – $12,000" },
      { name: "Medium Locs Detox", description: "$12,000 – $17,000" },
      { name: "Long Locs Detox", description: "$17,000 – $22,000" },
    ]
  },
  {
    category: "Stitch-in Extensions",
    icon: Scissors,
    items: [
      { name: "Half Stitch-in", description: "$9,000 – $13,000" },
      { name: "Full Stitch-in", description: "$13,000 – $17,000" },
      { name: "Closure Stitch-in", description: "$22,000" },
      { name: "Frontal Stitch-in", description: "$19,000" },
      { name: "Stitch-in Removal", description: "$5,000" },
    ]
  },
  {
    category: "Hair Extensions & Wigs",
    icon: Waves,
    items: [
      { name: "Tape-in Extensions", description: "Seamless tape application" },
      { name: "K-tip Extensions", description: "Keratin bond extensions" },
      { name: "Wig Installation", description: "Professional wig fitting" },
      { name: "Wig Coloring (Black/Brown)", description: "$8,000" },
      { name: "Wig Coloring (Blonde, Burgundy, Highlight, Watercolor)", description: "$18,000" },
      { name: "Wig Revamping", description: "Restore and refresh wigs" },
    ]
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const PriceList = ({ items, testIdPrefix }) => (
  <div className="grid md:grid-cols-2 gap-4">
    {items.map((service, index) => (
      <div 
        key={service.name}
        className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 border-b border-[#262626] last:border-0 gap-1 sm:gap-4"
        data-testid={`${testIdPrefix}-${index}`}
      >
        <span className="font-body text-white">{service.name}</span>
        <span className="font-heading text-lg text-[#C5A059] whitespace-nowrap">{service.price}</span>
      </div>
    ))}
  </div>
);

const SectionCTA = ({ bookCategory }) => (
  <div className="flex gap-3 mt-6">
    <Link
      to={`/book?service=${bookCategory}`}
      className="flex-1 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:brightness-110 transition-all"
    >
      Book Now
    </Link>
    <Link
      to="/contact"
      className="flex-1 border border-[#C5A059] text-[#C5A059] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:bg-[#C5A059]/10 transition-all"
    >
      Express Interest
    </Link>
  </div>
);

export const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Our Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Luxury Beauty<br />
              <span className="gold-gradient">Treatments</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Experience the finest beauty treatments in Georgetown & Mahaica. From facials to 
              nail services, we offer a complete range of luxury spa treatments 
              to help you look and feel your best. All prices in GYD.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Facial Services */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="facial-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Facial Treatments
              </h2>
            </div>

            <div className="bento-card p-8">
              <PriceList items={facialPrices} testIdPrefix="facial-price" />
              <p className="mt-6 text-xs text-[#A3A3A3] text-center">
                All prices in GYD
              </p>
              <SectionCTA bookCategory="facials" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Nail Services */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="nail-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Hand className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Nail Services
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bento-card p-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Nails
                </h3>
                <div className="space-y-4">
                  {nailPrices.map((service) => (
                    <div key={service.name} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
                      <span className="font-body text-sm text-white flex-1">{service.name}</span>
                      <span className="font-heading text-[#C5A059] text-sm whitespace-nowrap">{service.price}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bento-card p-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Manicure & Pedicure
                </h3>
                <div className="space-y-4">
                  {maniPediPrices.map((service) => (
                    <div key={service.name} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
                      <span className="font-body text-sm text-white flex-1">{service.name}</span>
                      <span className="font-heading text-[#C5A059] text-sm whitespace-nowrap">{service.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <SectionCTA bookCategory="nails" />
          </AnimatedSection>
        </div>
      </section>

      {/* Lash Extensions */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="lash-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Eye className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Lash Extensions
              </h2>
            </div>

            <div className="bento-card p-8">
              <PriceList items={lashPrices} testIdPrefix="lash-price" />
              <SectionCTA bookCategory="lashes" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Body Waxing */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="waxing-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Droplets className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Body Waxing
              </h2>
            </div>

            <div className="bento-card p-8">
              <PriceList items={waxingPrices} testIdPrefix="waxing-price" />
              <SectionCTA bookCategory="waxing" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Massage */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="massage-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Massage
              </h2>
            </div>

            <div className="bento-card p-8">
              <PriceList items={massagePrices} testIdPrefix="massage-price" />
              <SectionCTA bookCategory="massage" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Hair Chemical Services */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="hair-chemical-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                <Scissors className="w-6 h-6 text-[#C5A059]" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                Hair Treatments
              </h2>
            </div>

            {/* Big Bold Price Notice */}
            <div className="bg-[#C5A059]/10 border-2 border-[#C5A059] p-6 md:p-8 mb-8 text-center" data-testid="hair-price-notice">
              <p className="font-heading text-xl sm:text-2xl md:text-3xl text-[#C5A059] font-bold leading-snug">
                Price varies depending on hair type, texture, length, and condition.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bento-card p-8">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                  Chemical Treatments
                </h3>
                <PriceList items={hairChemicalPrices} testIdPrefix="hair-chemical-price" />
              </div>

              <div className="bento-card p-8">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                  Hair Coloring
                </h3>
                <PriceList items={hairColorPrices} testIdPrefix="hair-color-price" />
              </div>
            </div>
            <SectionCTA bookCategory="hair" />
          </AnimatedSection>
        </div>
      </section>

      {/* Hair & Extensions */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="other-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                More Services
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-white mt-2">
                Hair & Extensions
              </h2>
            </div>
          </AnimatedSection>

          <div className="space-y-12">
            {otherServices.map((category, index) => (
              <AnimatedSection key={category.category}>
                <div data-testid={`service-category-${index}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 border border-[#C5A059] flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-[#C5A059]" />
                    </div>
                    <h3 className="font-heading text-xl text-white">
                      {category.category}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.items.map((service) => (
                      <div 
                        key={service.name}
                        className="bento-card p-5 group"
                      >
                        <h4 className="font-heading text-lg text-white mb-1 group-hover:text-[#C5A059] transition-colors">
                          {service.name}
                        </h4>
                        <p className="font-body text-sm text-[#A3A3A3]">
                          {service.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}

            <AnimatedSection>
              <p className="text-center text-[#A3A3A3] text-sm mt-4">
                Contact us for pricing on hair and extension services
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="booking-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="bento-card p-12 md:p-16 text-center">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Book Your Appointment
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 mb-6">
                Ready to Pamper Yourself?
              </h2>
              <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-10">
                Contact us to book your appointment. We work by appointment to ensure 
                you receive personalized attention and the best experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/book"
                  data-testid="services-book-btn"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300"
                >
                  Book Appointment
                </Link>
                <Link
                  to="/contact"
                  data-testid="services-interest-btn"
                  className="border border-[#C5A059] text-[#C5A059] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#C5A059]/10 transition-all duration-300 inline-flex items-center justify-center gap-2"
                >
                  Express Interest
                </Link>
                <Link
                  to="/courses"
                  data-testid="services-learn-btn"
                  className="border border-[#262626] text-white font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:border-[#C5A059] transition-all duration-300 inline-flex items-center justify-center gap-2"
                >
                  Want to Learn? View Courses
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
